import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, Check, Sparkles, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * FeatureDiscoveryCard — Persistent Home dashboard card that guides new trial users
 * through the app's key features in a logical sequence.
 * 
 * Flow: SmartGoals → AI Meal Planning → Smart Coach → Workout Planner → Log a Meal
 * SmartGoals is the "gateway" — it must be completed first to unlock personalization.
 */
const STEPS = [
  {
    id: 'smartgoals',
    icon: '🧠',
    title: 'Set your SmartGoals',
    description: 'Teaches your AI what you need',
    unlockText: 'This unlocks all AI features',
    isGateway: true
  },
  {
    id: 'meal_plan',
    icon: '🍽️',
    title: 'Try AI Meal Planning',
    description: 'Helps calibrate your MetaBolicIQ'
  },
  {
    id: 'smart_coach',
    icon: '💬',
    title: 'Ask your Smart Coach',
    description: 'Your AI learns from every conversation'
  },
  {
    id: 'workout',
    icon: '💪',
    title: 'Generate a Workout',
    description: 'Activity data sharpens your MIQ score'
  },
  {
    id: 'log_meal',
    icon: '📸',
    title: 'Log your first meal',
    description: 'Real data powers real intelligence'
  }
];

const FeatureDiscoveryCard = ({
  userId,
  smartGoalsComplete,
  totalMealsLogged,
  isTrialUser,
  onSmartGoals,
  onMealPlan,
  onSmartCoach,
  onWorkout,
  onLogMeal
}) => {
  const storageKey = `feature_discovery_${userId}`;

  const [progress, setProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(`${storageKey}_dismissed`) === 'true';
    } catch { return false; }
  });

  // Persist progress
  const markComplete = useCallback((stepId) => {
    setProgress(prev => {
      const updated = { ...prev, [stepId]: true };
      try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, [storageKey]);

  // Auto-detect completions from app state
  useEffect(() => {
    if (smartGoalsComplete && !progress.smartgoals) {
      markComplete('smartgoals');
    }
    if (totalMealsLogged > 0 && !progress.log_meal) {
      markComplete('log_meal');
    }
  }, [smartGoalsComplete, totalMealsLogged, progress, markComplete]);

  const handleStepAction = useCallback((stepId) => {
    const actions = {
      smartgoals: onSmartGoals,
      meal_plan: onMealPlan,
      smart_coach: onSmartCoach,
      workout: onWorkout,
      log_meal: onLogMeal
    };
    // Mark as complete when user clicks (except SmartGoals which is auto-detected)
    if (stepId !== 'smartgoals' && stepId !== 'log_meal') {
      markComplete(stepId);
    }
    actions[stepId]?.();
  }, [onSmartGoals, onMealPlan, onSmartCoach, onWorkout, onLogMeal, markComplete]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try { localStorage.setItem(`${storageKey}_dismissed`, 'true'); } catch {}
  }, [storageKey]);

  // Build step statuses
  const stepsWithStatus = STEPS.map(step => ({
    ...step,
    complete: step.id === 'smartgoals' ? smartGoalsComplete :
              step.id === 'log_meal' ? (totalMealsLogged > 0 || progress.log_meal) :
              !!progress[step.id]
  }));

  const completedCount = stepsWithStatus.filter(s => s.complete).length;
  const allComplete = completedCount === STEPS.length;
  const gatewayComplete = smartGoalsComplete;

  // Find next recommended step
  const nextStep = stepsWithStatus.find(s => !s.complete);

  // Don't render if dismissed, not trial, or no userId
  // When all complete, show a final MIQ message instead of hiding
  if (!userId || !isTrialUser || dismissed) return null;

  const progressPercent = Math.round((completedCount / STEPS.length) * 100);

  // All steps done — show completion message
  if (allComplete) {
    return (
      <div
        data-testid="feature-discovery-card"
        style={{
          margin: '0 0 16px 0',
          padding: '16px',
          borderRadius: '16px',
          border: '1px solid rgba(56, 239, 125, 0.3)',
          background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.08) 0%, rgba(102, 126, 234, 0.08) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Sparkles size={18} color="#38ef7d" />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Your MetaBolicIQ is learning
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              Keep exploring so AI can better guide you
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'var(--text-secondary)', opacity: 0.5 }}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="feature-discovery-card"
      style={{
        margin: '0 0 16px 0',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid rgba(102, 126, 234, 0.25)',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'
      }}
    >
      {/* Header */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
          <Sparkles size={18} color="#667eea" />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              {completedCount === 0 ? 'Unlock your MetaBolicIQ' :
               `${completedCount} of ${STEPS.length} explored`}
            </div>
            {!collapsed && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                {!gatewayComplete
                  ? 'Complete SmartGoals to unlock all AI features'
                  : 'Keep exploring so AI can better guide you'}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Progress ring */}
          <div style={{ position: 'relative', width: '28px', height: '28px' }}>
            <svg width="28" height="28" viewBox="0 0 28 28">
              <circle cx="14" cy="14" r="11" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2.5" />
              <circle
                cx="14" cy="14" r="11"
                fill="none"
                stroke="#667eea"
                strokeWidth="2.5"
                strokeDasharray={`${progressPercent * 0.69115} 69.115`}
                strokeLinecap="round"
                transform="rotate(-90 14 14)"
                style={{ transition: 'stroke-dasharray 0.4s ease' }}
              />
            </svg>
            <span style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '9px', fontWeight: '700', color: '#667eea'
            }}>
              {completedCount}/{STEPS.length}
            </span>
          </div>

          {collapsed ? <ChevronDown size={16} color="var(--text-secondary)" /> : <ChevronUp size={16} color="var(--text-secondary)" />}

          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'var(--text-secondary)', opacity: 0.5 }}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Steps list */}
      {!collapsed && (
        <div style={{ padding: '0 12px 14px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {stepsWithStatus.map((step, i) => {
            const isNext = nextStep?.id === step.id;
            const isLocked = !gatewayComplete && !step.isGateway && !step.complete;

            return (
              <div
                key={step.id}
                data-testid={`discovery-step-${step.id}`}
                onClick={() => !step.complete && !isLocked && handleStepAction(step.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  cursor: step.complete ? 'default' : isLocked ? 'not-allowed' : 'pointer',
                  opacity: isLocked ? 0.45 : 1,
                  background: step.complete
                    ? 'rgba(56, 239, 125, 0.08)'
                    : isNext
                      ? 'rgba(102, 126, 234, 0.12)'
                      : 'rgba(255,255,255,0.03)',
                  border: isNext && !step.complete
                    ? '1.5px solid rgba(102, 126, 234, 0.4)'
                    : '1px solid rgba(255,255,255,0.06)',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Icon */}
                <div style={{
                  width: '36px', height: '36px',
                  borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px',
                  background: step.complete
                    ? 'rgba(56, 239, 125, 0.15)'
                    : isNext
                      ? 'rgba(102, 126, 234, 0.15)'
                      : 'rgba(255,255,255,0.05)',
                  flexShrink: 0
                }}>
                  {step.complete ? <Check size={18} color="#38ef7d" strokeWidth={3} /> : step.icon}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: step.complete ? '500' : '600',
                    color: step.complete ? 'var(--text-secondary)' : 'var(--text-primary)',
                    textDecoration: step.complete ? 'line-through' : 'none',
                    opacity: step.complete ? 0.7 : 1
                  }}>
                    {step.title}
                  </div>
                  {!step.complete && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '1px' }}>
                      {isLocked ? 'Complete SmartGoals first' : step.description}
                    </div>
                  )}
                </div>

                {/* Action arrow */}
                {!step.complete && !isLocked && (
                  <ChevronRight size={16} color={isNext ? '#667eea' : 'var(--text-secondary)'} style={{ flexShrink: 0 }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeatureDiscoveryCard;
